/**
 * Branch identity for one app id, resolved from the server.
 *
 * WHY THIS EXISTS
 *
 * Branch identity used to be a sessionStorage breadcrumb (`branch_ctx_<appId>`)
 * written by exactly three call sites: BranchesPicker.goBranch, BranchesPage and
 * BranchPickerModal. Anything else that reached a shadow app — a pasted URL, a
 * bookmark, a second tab, a deep link from the merge page — had no entry, and
 * two things went wrong:
 *
 *   1. The header rendered the plain "Branches" icon, indistinguishable from
 *      main, so there was no way to tell which app you were editing.
 *   2. Worse, ListApps excludes shadows (`is_branch = false OR is_branch IS
 *      NULL`, saas_service.go), so MainLayout's `currentApp` is ALWAYS
 *      undefined for a shadow. With no breadcrumb either, its
 *      `!currentApp && !branchCtx` check rendered the "app not found" panel.
 *
 * Branch identity is a property of the app, not of how you navigated to it, so
 * it is now read from GET /apps/:appId/branch-context.
 *
 * SHAPE
 *
 * Normalised to the shape the existing consumers (BranchesPicker, LocksPicker,
 * MainLayout) already destructure — `{branchName, parentAppId, parentAppName}`
 * — so this drops in without touching them. `null` means "this is a main app".
 */

import { useEffect, useState } from 'react';
import api from '../services/api';

const SESSION_PREFIX = 'branch_ctx_';

// appId → resolved ctx (or null). Survives soft navigation; wiped on reload,
// which is exactly when goBranch/goMain change the answer (both hard-reload).
const cache = new Map();
// appId → in-flight promise, so N components mounting at once share one request.
const inflight = new Map();

const sessionKey = (appId) => `${SESSION_PREFIX}${appId}`;

/** The pre-existing sessionStorage breadcrumb, if any. Used only for instant paint. */
export function readSessionBranchCtx(appId) {
    if (!appId) return null;
    try {
        const raw = sessionStorage.getItem(sessionKey(appId));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function normalize(payload, appId) {
    if (!payload || !payload.is_branch) return null;
    return {
        branchName: payload.branch_name,
        branchUid: payload.branch_uid,
        branchStatus: payload.branch_status,
        parentAppId: payload.parent_app_id,
        parentAppName: payload.parent_app_name,
        shadowAppId: appId,
    };
}

/**
 * Resolve branch identity for an app, cached per appId for the page's lifetime.
 * Never rejects — a failed lookup falls back to the sessionStorage breadcrumb
 * so a transient API error degrades to the old behaviour instead of wrongly
 * claiming a shadow app is main.
 */
export function fetchBranchContext(appId) {
    if (!appId) return Promise.resolve(null);
    const key = String(appId);
    if (cache.has(key)) return Promise.resolve(cache.get(key));
    if (inflight.has(key)) return inflight.get(key);

    const p = api.get(`/apps/${appId}/branch-context`)
        .then((res) => {
            const ctx = normalize(res.data, appId);
            cache.set(key, ctx);
            // Keep the breadcrumb in step with the server so the next mount
            // paints instantly and correctly — and so a stale entry on an app
            // that is NOT a branch stops colouring the header amber forever.
            try {
                if (ctx) sessionStorage.setItem(sessionKey(appId), JSON.stringify(ctx));
                else sessionStorage.removeItem(sessionKey(appId));
            } catch { /* private mode / quota — the cache still holds it */ }
            return ctx;
        })
        .catch(() => {
            // Do NOT cache a failure: the next mount should retry rather than
            // be stuck on the fallback for the rest of the session.
            return readSessionBranchCtx(appId);
        })
        .finally(() => inflight.delete(key));

    inflight.set(key, p);
    return p;
}

/**
 * @param {string|number|null} appId
 * @returns {{branchCtx: object|null, resolved: boolean}} `resolved` is false
 *   until the server has answered, so callers can avoid acting on the
 *   optimistic seed (e.g. rendering a "not found" panel too early).
 */
export function useBranchContext(appId) {
    // Seeded synchronously so a navigation that DID leave a breadcrumb paints
    // the branch chip on the first frame instead of flashing "Main".
    const [state, setState] = useState(() => {
        const key = String(appId);
        if (appId && cache.has(key)) return { branchCtx: cache.get(key), resolved: true };
        return { branchCtx: readSessionBranchCtx(appId), resolved: false };
    });

    useEffect(() => {
        if (!appId) {
            setState({ branchCtx: null, resolved: true });
            return undefined;
        }
        const key = String(appId);
        if (cache.has(key)) {
            setState({ branchCtx: cache.get(key), resolved: true });
            return undefined;
        }

        // Re-seed for the NEW appId before the request lands, otherwise the
        // previous app's chip stays on screen during the switch.
        setState({ branchCtx: readSessionBranchCtx(appId), resolved: false });

        let cancelled = false;
        fetchBranchContext(appId).then((ctx) => {
            if (!cancelled) setState({ branchCtx: ctx, resolved: true });
        });
        return () => { cancelled = true; };
    }, [appId]);

    return state;
}
